/**
 * On every page load: fetch cart and split any line with quantity > 1
 * into separate quantity-1 lines (when cartAlwaysNewLineItem is enabled).
 *
 * Runs on document ready and logs any errors without blocking other scripts.
 */
(function () {
  const LOG_PREFIX = '[cart-quantity-splitter]';

  function isCartPage() {
    const path = (window.location.pathname || '').replace(/\/$/, '');
    return (
      path === '/cart' ||
      document.body.classList.contains('template-cart') ||
      Boolean(document.getElementById('main-cart-items'))
    );
  }

  function enrichLineItem(item) {
    if (!item) return item;

    if (!item.product_type && window.CartProductTypes?.[item.key]) {
      return { ...item, product_type: window.CartProductTypes[item.key] };
    }

    return item;
  }

  function isPromoLine(item) {
    const enriched = enrichLineItem(item);
    const productType = String(
      enriched?.product_type || enriched?.product?.type || enriched?.type || window.CartProductTypes?.[enriched?.key] || ''
    )
      .trim()
      .toLowerCase();
    return productType === 'promo';
  }

  function shouldSplitLine(item) {
    return item && item.quantity > 1 && !isPromoLine(item);
  }

  function getCartUrl() {
    return (window.routes && window.routes.cart_url) || '/cart';
  }

  async function fetchCart() {
    const response = await fetch(`${getCartUrl()}.js`, { credentials: 'same-origin' });
    if (!response.ok) {
      throw new Error(`Fetch cart failed: ${response.statusText}`);
    }

    const cart = await response.json();
    if (Array.isArray(cart.items)) {
      cart.items = cart.items.map(enrichLineItem);
    }

    return cart;
  }

  function getCartFromLiquid() {
    if (!Array.isArray(window.CartData)) return null;

    return {
      items: window.CartData.map(enrichLineItem),
      source: 'liquid',
    };
  }

  async function getInitialCart() {
    const liquidCart = getCartFromLiquid();
    if (liquidCart) {
      console.log(`${LOG_PREFIX} Cart from Liquid`, liquidCart);
      return liquidCart;
    }

    const cart = await fetchCart();
    console.log(`${LOG_PREFIX} Cart fetched`, cart);
    return cart;
  }

  async function setLineQuantity(lineKey, quantity) {
    const response = await fetch('/cart/change.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: lineKey, quantity }),
    });

    if (!response.ok) {
      throw new Error(`Cart change failed: ${response.statusText}`);
    }

    return response.json();
  }

  async function rerenderCart(lastCartResponse) {
    if (!isCartPage()) return;

    console.log(`${LOG_PREFIX} Cart page detected — rerendering cart`);

    // We change quantity BEFORE adding the duplicate, so the last /cart/add.js
    // response sections already reflect the original line at qty 1. Reuse those
    // sections (the duplicator falls back to reloadCartSection if absent).
    if (window.CartLineDuplicator?.rerenderCart) {
      await window.CartLineDuplicator.rerenderCart(lastCartResponse || {}, null);
    }
  }

  async function splitLineItem(item) {
    if (isPromoLine(item)) {
      console.log(`${LOG_PREFIX} Skipping promo line`, {
        key: item?.key,
        sku: item?.sku,
        product_type: item?.product_type,
      });
      return null;
    }

    const duplicator = window.CartLineDuplicator;
    if (!duplicator?.buildLinePayload || !duplicator?.addLineToCart) {
      throw new Error('CartLineDuplicator helpers not available');
    }

    const extraCount = item.quantity - 1;
    if (extraCount <= 0) return null;

    console.log(`${LOG_PREFIX} Splitting line`, {
      key: item.key,
      sku: item.sku,
      title: item.title,
      quantity: item.quantity,
      extraLinesToAdd: extraCount,
    });

    await setLineQuantity(item.key, 1);

    let lastResponse = null;
    for (let i = 0; i < extraCount; i += 1) {
      const payload = duplicator.buildLinePayload(item);
      const addResponse = await duplicator.addLineToCart(payload);

      if (addResponse?.status) {
        throw new Error(addResponse.description || addResponse.message || 'Add to cart failed');
      }
      lastResponse = addResponse;
    }

    console.log(`${LOG_PREFIX} Split complete`, { key: item.key, newQuantity: 1 });

    return lastResponse;
  }

  function delay(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  async function splitCartLinesWithQuantityGreaterThanOne() {
    if (!window.cartAlwaysNewLineItem) return;

    // Give other cart/init scripts a moment to settle before reading the cart.
    await delay(300);

    const cart = await getInitialCart();

    if (!cart.items?.length) {
      console.log(`${LOG_PREFIX} Cart is empty — skipping`);
      return;
    }

    const linesToSplit = cart.items.filter(shouldSplitLine);
    const promoLinesSkipped = cart.items.filter((item) => item.quantity > 1 && isPromoLine(item));

    if (linesToSplit.length === 0) {
      if (promoLinesSkipped.length) {
        console.log(`${LOG_PREFIX} No splittable lines — ${promoLinesSkipped.length} promo line(s) with qty > 1 skipped`);
      } else {
        console.log(`${LOG_PREFIX} All cart lines already have quantity 1`);
      }
      return;
    }

    console.log(`${LOG_PREFIX} Found ${linesToSplit.length} line(s) with quantity > 1`, linesToSplit);

    let didSplit = false;
    let lastResponse = null;

    // Hard cap iterations to avoid an infinite loop if a line never drops to qty 1.
    const MAX_ITERATIONS = 100;
    let iterations = 0;

    while (iterations < MAX_ITERATIONS) {
      iterations += 1;

      const currentCart = await fetchCart();
      const nextLine = (currentCart.items || []).find(shouldSplitLine);
      if (!nextLine) break;

      const response = await splitLineItem(nextLine);
      if (response) {
        didSplit = true;
        lastResponse = response;
      } else {
        // No progress possible on this line — stop to avoid spinning.
        break;
      }
    }

    if (iterations >= MAX_ITERATIONS) {
      console.log(`${LOG_PREFIX} Reached max iterations (${MAX_ITERATIONS}) — stopping split loop`);
    }

    const finalCart = await fetchCart();
    const stillNeedsSplit = (finalCart.items || []).some(shouldSplitLine);

    if (!stillNeedsSplit) console.log(`${LOG_PREFIX} Cart split check complete`);

    if (didSplit) {
      await rerenderCart(lastResponse);
    }
  }

  function onDocumentReady(callback) {
    if (document.readyState === 'complete') {
      callback();
      return;
    }

    document.addEventListener('DOMContentLoaded', callback);
  }

  function init() {
    if (!window.cartAlwaysNewLineItem) return;

    splitCartLinesWithQuantityGreaterThanOne().catch((error) => {
      console.log(`${LOG_PREFIX}`, error);
    });
  }

  try {
    onDocumentReady(init);
  } catch (error) {
    console.log(`${LOG_PREFIX} init failed`, error);
  }
})();
